import React from 'react';
import loadable from '@loadable/component';
import type { LoaderFunctionArgs, RouteObject } from 'react-router-dom';
import { ANONYMIZED_PATHS, URLS } from 'Douleutaras/constants/urls';
import { LANGUAGE_URL_PREFIXES } from 'Douleutaras/constants/localization';

import { store as clientStore } from '../redux/store';
import { GetServerSideProps } from '../types/ssr';
import App from '../app/app';

// Loadable components — import() must be inline so @loadable/babel-plugin can
// statically add requireSync/chunkName metadata required for SSR rendering.
const Test            = loadable(() => import('../views/Test'));
const JobDone         = loadable(() => import('../views/job/jobdone/JobDone'));
const PostJobIframe   = loadable(() => import('../views/job/iframe/PostJobIframe'));
const PostJobWebview  = loadable(() => import('../views/job/webview/PostJobWebview'));
const ClientDashboard = loadable(() => import('../views/client-dashboard'));
const ProDashboard    = loadable(() => import('../views/seller-dashboard'));
const ProProfile      = loadable(() => import('../views/pro-profile/components/ProProfile'));
const AuthView        = loadable(() => import('../views/auth-view/AuthView/AuthView'));
const AutoLogin       = loadable(() => import('../views/auth-view/AutoLogin/AutoLogin')
    .then(m => m.AutoLogin));
const ContactUsPage   = loadable(() => import('../views/contact-us'));
const ReviewProPublic = loadable(() => import('../views/review-pro-public'));
const NotFound        = loadable(() => import('../layout/service-pages/NotFound')
    .then(m => m.NotFoundWrapped));

// SSR import functions — separate import() calls from those in loadable() above.
// These return the full ES module (including named exports like getServerSideProps).
// Webpack deduplicates modules at bundle time so no double-loading occurs.
const ssmTest            = () => import('../views/Test');
const ssmJobDone         = () => import('../views/job/jobdone/JobDone');
const ssmPostJobIframe   = () => import('../views/job/iframe/PostJobIframe');
const ssmPostJobWebview  = () => import('../views/job/webview/PostJobWebview');
const ssmClientDashboard = () => import('../views/client-dashboard');
const ssmProDashboard    = () => import('../views/seller-dashboard');
const ssmProProfile      = () => import('../views/pro-profile/components/ProProfile');
const ssmAuthView        = () => import('../views/auth-view/AuthView/AuthView');
const ssmAutoLogin       = () => import('../views/auth-view/AutoLogin/AutoLogin');
const ssmContactUs       = () => import('../views/contact-us');
const ssmReviewProPublic = () => import('../views/review-pro-public');
const ssmNotFound        = () => import('../layout/service-pages/NotFound');
const ssmDefaultFooter   = () => import('../layout/partials/DefaultFooter');

type StoreType = typeof clientStore;
interface SSRModule { getServerSideProps?: GetServerSideProps }

/**
 * Generic loader: imports the full module via the ssm* function and calls
 * getServerSideProps if exported. Components opt in simply by exporting
 * getServerSideProps — no registration needed anywhere else.
 */
const createAutoLoader = (importFn: () => Promise<SSRModule>, store?: StoreType) =>
  async ({ params }: LoaderFunctionArgs): Promise<null> => {
    if (typeof document !== 'undefined') {
      // Client-side navigation — components handle their own data fetching
      return null;
    }
    const mod = await importFn();
    if (typeof mod.getServerSideProps === 'function') {
      await mod.getServerSideProps({ store: store ?? clientStore, params });
    }
    return null;
  };

export const createRoutes = (store?: StoreType): Array<RouteObject> => [
  {
    element: <App />,
    // DefaultFooter renders on nearly every page via DefaultFullLayout but isn't itself
    // a routed view, so it can't export getServerSideProps to a leaf route entry below —
    // attached here, on the common root layout route, so it runs for every request.
    loader: createAutoLoader(ssmDefaultFooter, store),
    children: [
      { path: '/dashboard-v2/test',               element: <Test />,            loader: createAutoLoader(ssmTest, store) },
      { path: URLS.JOB_DONE,                      element: <JobDone />,         loader: createAutoLoader(ssmJobDone, store) },
      { path: URLS.AUTHENTICATION.IFRAME,         element: <AuthView />,        loader: createAutoLoader(ssmAuthView, store) },
      { path: URLS.AUTHENTICATION.LOGIN,          element: <AuthView />,        loader: createAutoLoader(ssmAuthView, store) },
      { path: URLS.AUTHENTICATION.REDIRECT,       element: <AutoLogin />,       loader: createAutoLoader(ssmAutoLogin, store) },
      { path: URLS.CONTACT_US.INDEX,              element: <ContactUsPage />,   loader: createAutoLoader(ssmContactUs, store) },
      { path: URLS.POST_JOB.WEBVIEW,              element: <PostJobWebview />,  loader: createAutoLoader(ssmPostJobWebview, store) },
      { path: URLS.POST_JOB.IFRAME,               element: <PostJobIframe />,   loader: createAutoLoader(ssmPostJobIframe, store) },
      { path: URLS.PUBLIC_REVIEW,                 element: <ReviewProPublic />, loader: createAutoLoader(ssmReviewProPublic, store) },
      // handle.anonymized: cached at the Cloudflare edge, must render identically for every
      // visitor — src/server/index.tsx reads this flag (via matchRoutes) to skip resolving auth
      // server-side. handle.languagePrefixed marks the /en-style variant, where language comes
      // from the URL prefix instead of the languagecode cookie. ANONYMIZED_PATHS is the single
      // source of truth for these paths, also used by src/server/index.tsx's Express mounts.
      ...ANONYMIZED_PATHS.flatMap(path => [
        { path: `${path}/*`, element: <ProProfile />, loader: createAutoLoader(ssmProProfile, store), handle: { anonymized: true } },
        ...LANGUAGE_URL_PREFIXES.map(prefix => ({
          path: `/${prefix}${path}/*`,
          element: <ProProfile />,
          loader: createAutoLoader(ssmProProfile, store),
          handle: { anonymized: true, languagePrefixed: true }
        }))
      ]),
      { path: `${URLS.PRO_DASHBOARD.INDEX}/*`,    element: <ProDashboard />,    loader: createAutoLoader(ssmProDashboard, store) },
      { path: `${URLS.CLIENT_DASHBOARD.INDEX}/*`, element: <ClientDashboard />, loader: createAutoLoader(ssmClientDashboard, store) },
      { path: '*',                                element: <NotFound />,        loader: createAutoLoader(ssmNotFound, store) },
    ],
  },
];
